// The React edge of tuneKnowledge.ts: resolve the loaded tune against the
// curated manifest, then fetch the summaries for whatever it matched. Both steps
// are cached per session, so reopening the drawer costs nothing.

import { useEffect, useState } from 'react';
import {
  lookupComposerEntries,
  lookupTuneEntry,
  tuneKnowledgeIndex,
  tuneProse,
  wikipediaSummary,
  type TuneProseEntry,
  type TuneRecording,
  type WikipediaSummary,
} from './tuneKnowledge';

export interface TuneKnowledge {
  tune: WikipediaSummary | null;
  /**
   * Headed by the ARTICLE's title, not the chart's credit: the library inherits
   * iReal's spellings, and "Thelonius Monk" printed over Thelonious Monk's
   * biography reads like the wrong link.
   */
  composers: WikipediaSummary[];
  /** Versions to hear, from the article's catalogue — empty when there are none. */
  recordings: TuneRecording[];
}

const EMPTY: TuneKnowledge = { tune: null, composers: [], recordings: [] };

/**
 * The tune's paragraph comes from the build-time harvest — the article's own
 * section on the music — while the live summary supplies what only it has: the
 * lead image and Wikidata's one-liner. Where the harvest has nothing, the live
 * lead stands; where the network has nothing, the harvest still prints, so a
 * tune's prose survives being offline. Writers keep their lead: a person's
 * opening sentence is a life, not a definition of a thing you already know.
 */
function pageTitle(page: string): string {
  return page.replace(/\s*\([^)]*\)\s*$/, '');
}

function pageUrl(page: string): string {
  return `https://en.wikipedia.org/wiki/${encodeURIComponent(page.replace(/ /g, '_'))}`;
}

function withHarvestedProse(
  summary: WikipediaSummary | null,
  harvested: TuneProseEntry | undefined,
  page: string,
): WikipediaSummary | null {
  if (!harvested) return summary;
  if (harvested.text) {
    if (!summary) {
      return {
        title: pageTitle(page),
        extract: harvested.text,
        section: harvested.section,
        url: pageUrl(page),
      };
    }
    return { ...summary, extract: harvested.text, section: harvested.section };
  }
  // Catalogue only: keep the live image and link, but do not print the lead —
  // it is the definition the masthead already covers.
  if (summary) return { ...summary, extract: '', section: '' };
  if (harvested.recordings?.length) {
    return { title: pageTitle(page), extract: '', url: pageUrl(page) };
  }
  return null;
}

/**
 * Prose for one tune, in TWO stages, because the two halves come from very
 * different places. Everything printed as text — the tune's paragraph and its
 * recordings — is bundled with the app; only the lead image and Wikidata's
 * one-liner need Wikipedia. Resolving both together behind one `setState` meant
 * the local half waited on a third-party round-trip: on a blocked network the
 * drawer stayed empty for ten seconds and then filled the instant the
 * connection RESET, and on a network that hangs it would never fill at all.
 *
 * So stage 1 renders what is on disk as soon as it is read, and stage 2 folds
 * the live summaries in when (and if) they arrive. `loading` describes stage 1:
 * it is false once the reader has the text.
 *
 * A tune with no manifest entry settles to empty, which the panel renders as
 * nothing rather than as a failure — most of the library has no article, and
 * that is not an error worth reporting.
 */
export function useTuneKnowledge(
  title: string,
  composer: string | undefined,
  enabled = true,
): { knowledge: TuneKnowledge; loading: boolean } {
  const [knowledge, setKnowledge] = useState<TuneKnowledge>(EMPTY);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled || !title) {
      setKnowledge(EMPTY);
      setLoading(false);
      return;
    }
    let live = true;
    setKnowledge(EMPTY);
    setLoading(true);
    void (async () => {
      const [index, harvested] = await Promise.all([tuneKnowledgeIndex(), tuneProse()]);
      if (!live) return;
      const tuneEntry = lookupTuneEntry(index, title, composer);
      const writers = lookupComposerEntries(index, composer);
      const entry = tuneEntry ? harvested[tuneEntry.wikipedia] : undefined;
      const recordings = entry?.recordings ?? [];

      // Stage 1: the bundled harvest, standing on its own.
      setKnowledge({
        tune: tuneEntry ? withHarvestedProse(null, entry, tuneEntry.wikipedia) : null,
        composers: [],
        recordings,
      });
      setLoading(false);
      if (!tuneEntry && writers.length === 0) return;

      // Stage 2: the picture and the one-liner, if the network obliges.
      const [summary, ...prose] = await Promise.all([
        tuneEntry ? wikipediaSummary(tuneEntry.wikipedia) : Promise.resolve(null),
        ...writers.map((writer) => wikipediaSummary(writer.wikipedia)),
      ]);
      if (!live) return;
      setKnowledge({
        tune: tuneEntry ? withHarvestedProse(summary, entry, tuneEntry.wikipedia) : null,
        composers: prose.filter((entry) => entry !== null),
        recordings,
      });
    })();
    return () => {
      live = false;
    };
  }, [title, composer, enabled]);

  return { knowledge, loading };
}
